import React, {
  useState, useRef, useEffect, useMemo, useCallback,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useSelector } from 'react-redux';
import {
  Search, X, Check, Package, ShoppingCart,
  SlidersHorizontal, ChevronDown, Loader2,
  Tag, Layers, Paintbrush, BarChart3, Grid2X2,
  ExternalLink,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import Apiurl from '../../Helpers/apiUrl';
import { Preview } from '@mui/icons-material';
import { formatCurrency } from '../../Helpers/currencyHelper';

const CSS = `
:root{
  --cb:#4F46E5;--cb-dk:#3730A3;--cb-lt:#EEF2FF;
  --ink:#111827;--sl:#374151;--mu:#6B7280;
  --bo:#E5E7EB;--bo-dk:#D1D5DB;
  --bg:#F9FAFB;--su:#FFFFFF;--hv:#F3F4F6;
  --gr:#059669;--gr-bg:#ECFDF5;
  --am:#D97706;--am-bg:#FFFBEB;
  --re:#DC2626;--re-bg:#FEF2F2;
  --te:#0D9488;--bl:#0284C7;
  --f:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;
  --fm:'SF Mono','Fira Code','Cascadia Code',monospace;
}
@keyframes psm-spin { to { transform: rotate(360deg); } }
.psm-spin { animation: psm-spin 1s linear infinite; }
.psm-bd{
  position:fixed;inset:0;background:rgba(17,24,39,.5);
  backdrop-filter:blur(3px);z-index:9000;
  display:flex;align-items:center;justify-content:center;padding:16px;
}
.psm-modal{
  width:100%;max-width:920px;height:90vh;max-height:880px;
  background:var(--su);border-radius:18px;
  box-shadow:0 20px 60px rgba(17,24,39,.2),0 0 0 1px rgba(17,24,39,.06);
  display:flex;flex-direction:column;overflow:hidden;
  font-family:var(--f);
}
/* header */
.psm-hd{
  padding:18px 24px 16px;flex-shrink:0;
  background:#111827;
  display:flex;align-items:flex-start;justify-content:space-between;
}
.psm-hd-icon{
  width:34px;height:34px;border-radius:9px;
  background:rgba(255,255,255,.18);
  display:flex;align-items:center;justify-content:center;flex-shrink:0;
}
.psm-hd-title{font-size:17px;font-weight:800;color:#fff;letter-spacing:-.02em;line-height:1;}
.psm-hd-sub{font-size:12px;color:rgba(255,255,255,.65);margin-top:4px;}
.psm-hd-badge{
  font-size:11px;font-weight:700;
  background:rgba(255,255,255,.2);color:#fff;
  padding:2px 8px;border-radius:20px;margin-left:8px;
}
.psm-hd-close{
  width:30px;height:30px;border-radius:8px;
  background:rgba(255,255,255,.15);border:1px solid rgba(255,255,255,.2);
  cursor:pointer;color:#fff;
  display:flex;align-items:center;justify-content:center;
  transition:background .15s;
}
.psm-hd-close:hover{background:rgba(255,255,255,.28);}
/* search bar */
.psm-sb{
  padding:11px 20px;border-bottom:1px solid var(--bo);
  display:flex;gap:10px;align-items:center;flex-shrink:0;
}
.psm-sf{
  flex:1;display:flex;align-items:center;gap:8px;
  background:var(--bg);border:1.5px solid var(--bo);
  border-radius:10px;padding:0 12px;height:38px;
  transition:border-color .15s;
}
.psm-sf:focus-within{border-color:var(--cb);}
.psm-si{
  flex:1;border:none;background:transparent;outline:none;
  font-family:var(--f);font-size:13px;color:var(--ink);
}
.psm-si::placeholder{color:var(--mu);}
.psm-sc{
  background:var(--bo);border:none;width:18px;height:18px;
  border-radius:5px;display:flex;align-items:center;justify-content:center;
  cursor:pointer;flex-shrink:0;
}
.psm-fb{
  display:flex;align-items:center;gap:6px;height:38px;
  padding:0 14px;border-radius:10px;cursor:pointer;
  font-family:var(--f);font-size:12px;font-weight:600;
  border:1.5px solid var(--bo);background:var(--su);color:var(--sl);
  transition:all .15s;white-space:nowrap;flex-shrink:0;
}
.psm-fb.on{border-color:var(--cb);background:var(--cb-lt);color:var(--cb);}
.psm-fc{
  background:var(--cb);color:#fff;font-size:10px;font-weight:800;
  width:18px;height:18px;border-radius:50%;
  display:flex;align-items:center;justify-content:center;
}
/* filters panel */
.psm-fp{
  padding:12px 20px 10px;background:#FAFBFF;
  border-bottom:1px solid var(--bo);flex-shrink:0;
}
.psm-fr{
  display:flex;gap:8px;overflow-x:auto;padding-bottom:2px;
  scrollbar-width:none;
}
.psm-fr::-webkit-scrollbar{display:none;}
.psm-chips{display:flex;flex-wrap:wrap;gap:6px;margin-top:10px;align-items:center;}
.psm-chip{
  display:inline-flex;align-items:center;gap:4px;
  font-size:10px;font-weight:700;
  padding:3px 8px;border-radius:20px;cursor:pointer;
  font-family:var(--f);border:1px solid;transition:opacity .1s;
}
.psm-chip:hover{opacity:.75;}
.psm-ca{
  margin-left:auto;border:1px solid #FCA5A5;background:#FEF2F2;
  color:var(--re);font-size:10px;font-weight:700;
  padding:3px 10px;border-radius:20px;cursor:pointer;
  font-family:var(--f);white-space:nowrap;
}
/* controls bar */
.psm-cb{
  padding:7px 20px;background:var(--bg);
  border-bottom:1px solid var(--bo);
  display:flex;align-items:center;gap:14px;flex-shrink:0;
}
.psm-sa{display:flex;align-items:center;gap:7px;cursor:pointer;user-select:none;}
.psm-sa-lbl{font-size:12px;font-weight:600;color:var(--sl);}
.psm-ckb{
  width:17px;height:17px;border-radius:5px;flex-shrink:0;
  border:2px solid var(--bo-dk);background:var(--su);
  display:flex;align-items:center;justify-content:center;
  transition:all .12s;
}
.psm-ckb.on{border-color:var(--cb);background:var(--cb);}
/* number input */
.psm-nw{display:flex;flex-direction:column;gap:3px;}
.psm-nl{
  font-size:10px;font-weight:700;color:var(--mu);
  letter-spacing:.06em;text-transform:uppercase;
}
.psm-nf{
  display:flex;align-items:center;
  border:1.5px solid var(--bo);border-radius:9px;
  background:var(--su);overflow:hidden;transition:border-color .15s;
}
.psm-nf:focus-within{border-color:var(--cb);}
.psm-nb{
  width:28px;height:32px;border:none;flex-shrink:0;
  background:var(--bg);cursor:pointer;
  font-size:15px;color:var(--mu);
  display:flex;align-items:center;justify-content:center;
  transition:background .1s,color .1s;
}
.psm-nb:hover{background:var(--cb-lt);color:var(--cb);}
.psm-nb.l{border-right:1px solid var(--bo);}
.psm-nb.r{border-left:1px solid var(--bo);}
.psm-ni{
  flex:1;border:none;outline:none;background:transparent;
  text-align:center;font-family:var(--fm);
  font-size:13px;font-weight:700;color:var(--ink);
  padding:4px;-moz-appearance:textfield;
}
.psm-ni::-webkit-inner-spin-button,.psm-ni::-webkit-outer-spin-button{-webkit-appearance:none;margin:0;}
.psm-ns{font-size:11px;color:var(--mu);font-weight:600;padding-right:8px;pointer-events:none;flex-shrink:0;}
/* list */
.psm-list{flex:1;overflow-y:auto;overflow-x:hidden;background:var(--su);}
.psm-list::-webkit-scrollbar{width:5px;}
.psm-list::-webkit-scrollbar-thumb{background:var(--bo-dk);border-radius:4px;}
/* row */
.psm-row{
  display:flex;align-items:center;gap:12px;
  padding:10px 20px;border-bottom:1px solid var(--bo);
  background:var(--su);border-left:3px solid transparent;
  transition:background .1s;cursor:pointer;
}
.psm-row:hover:not(.ex){background:var(--hv);}
.psm-row.sel{background:var(--cb-lt);border-left-color:var(--cb);}
.psm-row.ex{opacity:.5;cursor:not-allowed;}
.psm-ri{
  width:44px;height:44px;border-radius:10px;flex-shrink:0;
  background:var(--bg);border:1px solid var(--bo);
  display:flex;align-items:center;justify-content:center;overflow:hidden;
}
.psm-ri img{width:100%;height:100%;object-fit:cover;}
.psm-rb{flex:1;min-width:0;}
.psm-rt{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;}
.psm-rl{min-width:0;flex:1;}
.psm-br{display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-bottom:3px;}
.psm-sku{
  display:inline-flex;align-items:center;
  font-family:var(--fm);font-size:10px;font-weight:700;
  color:var(--cb);background:var(--cb-lt);
  padding:1px 6px;border-radius:4px;letter-spacing:.02em;
}
.psm-stk{
  display:inline-flex;align-items:center;gap:4px;
  font-size:9px;font-weight:700;letter-spacing:.04em;
  padding:2px 7px;border-radius:20px;
}
.psm-stk.in{background:var(--gr-bg);color:var(--gr);}
.psm-stk.low{background:var(--am-bg);color:var(--am);}
.psm-stk.out{background:var(--re-bg);color:var(--re);}
.psm-dot{width:5px;height:5px;border-radius:50%;flex-shrink:0;}
.psm-ab{
  font-size:9px;font-weight:700;color:var(--mu);
  background:var(--bg);border:1px solid var(--bo);
  padding:1px 6px;border-radius:20px;
}
.psm-nm{
  font-size:13px;font-weight:600;color:var(--ink);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-bottom:3px;
}
.psm-mt{display:flex;align-items:center;gap:6px;flex-wrap:wrap;font-size:11px;color:var(--mu);}
.psm-mtb{font-weight:600;color:var(--ink);}
.psm-mts{color:var(--bo-dk);font-size:10px;}
.psm-tags{display:flex;gap:4px;margin-top:5px;flex-wrap:wrap;}
.psm-tag{font-size:9.5px;font-weight:600;padding:2px 6px;border-radius:5px;border:1px solid;}
.psm-pr{text-align:right;flex-shrink:0;}
.psm-mrp{font-family:var(--fm);font-size:14px;font-weight:800;color:var(--ink);}
.psm-base{font-size:10px;color:var(--mu);margin-top:2px;}
/* footer */
.psm-ft{
  padding:12px 20px;background:var(--su);
  border-top:1px solid var(--bo);
  display:flex;align-items:center;justify-content:space-between;flex-shrink:0;
}
.psm-fi{font-size:13px;color:var(--mu);}
.psm-fic{font-family:var(--fm);font-weight:800;color:var(--cb);}
.psm-fit{font-weight:700;color:var(--sl);}
.psm-bc{
  height:38px;padding:0 18px;border-radius:10px;
  border:1.5px solid var(--bo);background:var(--su);
  font-family:var(--f);font-size:13px;font-weight:600;color:var(--sl);
  cursor:pointer;transition:border-color .15s;
}
.psm-bc:hover{border-color:var(--bo-dk);}
.psm-ba{
  height:38px;padding:0 20px;border-radius:10px;border:none;
  background:linear-gradient(135deg,var(--cb-dk) 0%,var(--cb) 100%);
  color:#fff;font-family:var(--f);font-size:13px;font-weight:700;
  display:flex;align-items:center;gap:8px;
  box-shadow:0 4px 14px rgba(79,70,229,.35);
  cursor:pointer;transition:opacity .2s,box-shadow .2s;
}
.psm-ba:hover:not(:disabled){opacity:.9;box-shadow:0 6px 20px rgba(79,70,229,.45);}
.psm-ba:disabled{background:var(--bo);color:var(--mu);box-shadow:none;cursor:not-allowed;}
/* empty */
.psm-em{
  display:flex;flex-direction:column;align-items:center;
  justify-content:center;gap:10px;padding:60px 20px;color:var(--mu);
}
.psm-em-t{font-size:14px;font-weight:600;}
.psm-em-s{font-size:12px;}
/* ── dropdown popup (portal) ── */
.psm-dw{position:relative;flex:1;min-width:140px;}
.psm-dt{
  width:100%;display:flex;align-items:center;gap:6px;
  border:1.5px solid var(--bo);border-radius:9px;
  padding:7px 10px;cursor:pointer;
  font-family:var(--f);font-size:12px;color:var(--mu);
  background:var(--su);transition:all .15s;font-weight:400;
  white-space:nowrap;overflow:hidden;
}
.psm-dt.on{border-color:rgba(79,70,229,.45);background:var(--cb-lt);color:var(--cb);font-weight:600;}
.psm-dtl{flex:1;text-align:left;overflow:hidden;text-overflow:ellipsis;}
.psm-dtx{
  display:flex;align-items:center;padding:2px;
  border-radius:4px;background:rgba(79,70,229,.15);cursor:pointer;flex-shrink:0;
}
.psm-dch{flex-shrink:0;opacity:.45;transition:transform .2s;}
.psm-dch.op{transform:rotate(180deg);}
.psm-dp{
  position:fixed;z-index:99999;
  background:var(--su);border:1.5px solid var(--bo-dk);
  border-radius:12px;
  box-shadow:0 8px 30px rgba(17,24,39,.14),0 2px 8px rgba(17,24,39,.06);
  overflow:hidden;min-width:200px;
}
.psm-dsw{padding:8px 8px 4px;border-bottom:1px solid var(--bo);}
.psm-ds{
  display:flex;align-items:center;gap:6px;
  background:var(--bg);border:1.5px solid var(--bo);
  border-radius:8px;padding:5px 8px;
}
.psm-dsi{
  flex:1;height:20%;border:none;background:transparent;outline:none;
  font-family:var(--f);font-size:11px;color:var(--ink);
}
.psm-dsi::placeholder{color:var(--mu);}
.psm-dsa{
  width:100%;display:flex;align-items:center;gap:8px;
  padding:7px 10px;border:none;border-bottom:1px solid var(--bo);
  cursor:pointer;font-family:var(--f);font-size:11px;
  color:var(--mu);background:transparent;transition:background .1s;
}
.psm-dsa:hover{background:var(--hv);}
.psm-dl{max-height:200px;overflow-y:auto;}
.psm-dl::-webkit-scrollbar{width:4px;}
.psm-dl::-webkit-scrollbar-thumb{background:var(--bo-dk);border-radius:4px;}
.psm-do{
  width:100%;display:flex;align-items:center;gap:8px;
  padding:7px 10px;border:none;border-bottom:1px solid var(--bo);
  cursor:pointer;font-family:var(--f);font-size:12px;
  color:var(--ink);background:transparent;text-align:left;
  transition:background .1s;overflow:hidden;
}
.psm-do:hover{background:var(--hv);}
.psm-do.on{background:var(--cb-lt);color:var(--cb);}
.psm-dol{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.psm-de{padding:14px 10px;text-align:center;font-size:11px;color:var(--mu);}
.psm-diag-link {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 9px;
  font-weight: 700;
  color: var(--cb);
  text-align: center;
  margin-top: 3px;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.15s ease;
}
.psm-diag-link:hover {
  color: var(--cb-dk);
  text-decoration: underline;
}
`;

/* ─── Tiny checkbox ──────────────────────────────────────────────────────── */
const CB = ({ checked, inter }) => (
  <div className={`psm-ckb${checked || inter ? ' on' : ''}`}>
    {checked && <Check size={9} color="white" strokeWidth={4} />}
    {inter && !checked && <div style={{ width: 7, height: 1.5, background: 'white', borderRadius: 1 }} />}
  </div>
);

const stkCls = s => s === 'In Stock' || s === 'MTS' ? 'in' : s === 'Low Stock' ? 'low' : 'out';
const stkDot = s => s === 'In Stock' || s === 'MTS' ? '#10B981' : s === 'Low Stock' ? '#F59E0B' : '#EF4444';

const MultiSelectDropdown = React.memo(function MultiSelectDropdown({
  label, icon: Icon, options = [], value = [], onChange,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pos, setPos] = useState({});
  const trigRef = useRef(null);
  const srchRef = useRef(null);

  const calcPos = useCallback(() => {
    if (!trigRef.current) return;
    const r = trigRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 220) });
  }, []);

  const handleOpen = () => { calcPos(); setOpen(o => !o); };

  useEffect(() => {
    if (open) { setTimeout(() => srchRef.current?.focus(), 40); }
    else setSearch('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const out = e => {
      if (!trigRef.current?.contains(e.target) &&
        !document.getElementById('psm-portal')?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', out);
    window.addEventListener('scroll', calcPos, true);
    window.addEventListener('resize', calcPos);
    return () => {
      document.removeEventListener('mousedown', out);
      window.removeEventListener('scroll', calcPos, true);
      window.removeEventListener('resize', calcPos);
    };
  }, [open, calcPos]);

  const filtered = useMemo(
    () => options.filter(o => o.toLowerCase().includes(search.toLowerCase())),
    [options, search],
  );

  const allSel = filtered.length > 0 && filtered.every(o => value.includes(o));
  const someSel = filtered.some(o => value.includes(o));

  const toggle = useCallback(opt =>
    onChange(value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt]),
    [value, onChange]);

  const toggleAll = useCallback(() => {
    if (allSel) onChange(value.filter(v => !filtered.includes(v)));
    else onChange([...value, ...filtered.filter(o => !value.includes(o))]);
  }, [allSel, value, filtered, onChange]);

  const hasVal = value.length > 0;
  const lbl = !hasVal ? `All ${label}`
    : value.length === 1 ? value[0]
      : `${value.length} ${label}`;

  useEffect(() => {
    if (!document.getElementById('psm-portal')) {
      const el = document.createElement('div');
      el.id = 'psm-portal';
      document.body.appendChild(el);
    }
  }, []);

  const portalEl = typeof document !== 'undefined'
    ? (document.getElementById('psm-portal') || document.body)
    : null;

  return (
    <div className="psm-dw">
      <button ref={trigRef} className={`psm-dt${hasVal ? ' on' : ''}`} onClick={handleOpen}>
        {Icon && <Icon size={12} style={{ flexShrink: 0, opacity: 0.65 }} />}
        <span className="psm-dtl">{lbl}</span>
        {hasVal && (
          <span className="psm-dtx" onClick={e => { e.stopPropagation(); onChange([]); }}>
            <X size={10} color="#4F46E5" />
          </span>
        )}
        <ChevronDown size={12} className={`psm-dch${open ? ' op' : ''}`} />
      </button>

      {open && portalEl && createPortal(
        <div className="psm-dp" style={pos}>
          <div className="psm-dsw">
            <div className="psm-ds">
              <Search size={11} color="var(--mu)" />
              <input ref={srchRef} className="psm-dsi" value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}…`} />
              {search && <X size={10} color="var(--mu)" style={{ cursor: 'pointer' }} onClick={() => setSearch('')} />}
            </div>
          </div>
          {filtered.length > 1 && (
            <button className="psm-dsa" onClick={toggleAll}>
              <CB checked={allSel} inter={someSel && !allSel} />
              <span style={{ fontWeight: 600 }}>Select all ({filtered.length})</span>
            </button>
          )}
          <div className="psm-dl">
            {filtered.length === 0
              ? <div className="psm-de">No results</div>
              : filtered.map(opt => (
                <button key={opt} className={`psm-do${value.includes(opt) ? ' on' : ''}`}
                  onClick={() => toggle(opt)}>
                  <CB checked={value.includes(opt)} />
                  <span className="psm-dol">{opt}</span>
                </button>
              ))
            }
          </div>
        </div>,
        portalEl,
      )}
    </div>
  );
});

/* ─── Number input ───────────────────────────────────────────────────────── */
const NumberInput = React.memo(function NumberInput({
  label, value, onChange, min = 0, max, step = 1, suffix,
}) {
  const [local, setLocal] = useState(String(value));
  useEffect(() => setLocal(String(value)), [value]);

  const commit = useCallback(raw => {
    let n = parseFloat(raw);
    if (isNaN(n)) n = min ?? 0;
    if (min != null) n = Math.max(min, n);
    if (max != null) n = Math.min(max, n);
    onChange(n); setLocal(String(n));
  }, [min, max, onChange]);

  return (
    <div className="psm-nw">
      <span className="psm-nl">{label}</span>
      <div className="psm-nf">
        <button className="psm-nb l" onClick={() => commit(String(value - step))}>−</button>
        <input type="number" className="psm-ni"
          value={local}
          onChange={e => setLocal(e.target.value)}
          onBlur={e => commit(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && commit(local)}
          style={{ width: suffix ? 44 : 52 }}
        />
        {suffix && <span className="psm-ns">{suffix}</span>}
        <button className="psm-nb r" onClick={() => commit(String(value + step))}>+</button>
      </div>
    </div>
  );
});

/* tag config (stable, outside component) */
const TAG_COLORS = {
  cat: { color: '#0D9488', bg: '#F0FDFA', bdr: '#99F6E4' },
  type: { color: '#0284C7', bg: '#EFF6FF', bdr: '#BAE6FD' },
  seg: { color: '#92400E', bg: '#FFFBEB', bdr: '#FDE68A' },
  gst: { color: '#6B7280', bg: '#F9FAFB', bdr: '#E5E7EB' },
};

/* ─── Product Row ────────────────────────────────────────────────────────── */
const ProductRow = React.memo(function ProductRow({ p, isSel, isExist, onToggle }) {
  return (
    <div
      className={`psm-row${isSel ? ' sel' : ''}${isExist ? ' ex' : ''}`}
      onClick={() => !isExist && onToggle(p.sku_code)}
    >
      {/* checkbox */}
      <div className={`psm-ckb${isSel || isExist ? ' on' : ''}`}
        style={isSel ? { boxShadow: '0 0 0 3px rgba(79,70,229,.18)' } : undefined}>
        {(isSel || isExist) && <Check size={9} color="white" strokeWidth={4} />}
      </div>

      {/* image */}
      {/* image */}
      <div className="psm-ri-wrap" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div className="psm-ri">
          {(() => {
            const fallback = Apiurl.replace('/api/', '/img/no_image.jpeg');
            const imageSrc = p.image ? (p.image.startsWith('http') ? p.image : `${Apiurl}${p.image}`) : fallback;
            return (
              <img
                src={imageSrc}
                alt=""
                className="w-full h-full object-cover"
                onError={e => {
                  if (e.target.src !== fallback) {
                    e.target.src = fallback;
                  }
                }}
              />
            );
          })()}
        </div>
        {p.linediagram_image && (
          <span
            className="psm-diag-link"
            onClick={(e) => {
              e.stopPropagation();
              const diagSrc = p.linediagram_image.startsWith('http')
                ? p.linediagram_image
                : `${Apiurl}${p.linediagram_image}`;
              window.open(diagSrc, '_blank');
            }}
          >
            Line Diagram
            <ExternalLink size={9} strokeWidth={2.5} />
          </span>
        )}
      </div>

      {/* body */}
      <div className="psm-rb">
        <div className="psm-rt">
          <div className="psm-rl">
            <div className="psm-br">
              <span className="psm-sku">{p.sku_code}</span>
              {isExist && <span className="psm-ab">Already Added</span>}
            </div>
            <div className="psm-nm">{p.product_desc}</div>
            <div className="psm-mt">
              {p.brand && <span className="psm-mtb">{p.brand}</span>}
              {p.collection && <><span className="psm-mts">·</span><span>{p.collection}</span></>}
              {p.finish && p.finish !== 'NOT FOUND' && <><span className="psm-mts">·</span><span>{p.finish}</span></>}
            </div>
            <div className="psm-tags">
              {p.category && <span className="psm-tag" style={{ color: TAG_COLORS.cat.color, background: TAG_COLORS.cat.bg, borderColor: TAG_COLORS.cat.bdr }}>{p.category}</span>}
              {p.product_typ_group && <span className="psm-tag" style={{ color: TAG_COLORS.type.color, background: TAG_COLORS.type.bg, borderColor: TAG_COLORS.type.bdr }}>{p.product_typ_group}</span>}
              {p.priceSegment && <span className="psm-tag" style={{ color: TAG_COLORS.seg.color, background: TAG_COLORS.seg.bg, borderColor: TAG_COLORS.seg.bdr }}>{p.priceSegment}</span>}
              {p.gst && <span className="psm-tag" style={{ color: TAG_COLORS.gst.color, background: TAG_COLORS.gst.bg, borderColor: TAG_COLORS.gst.bdr }}>{p.gst}% GST</span>}
            </div>
          </div>
          <div className="psm-pr">
            <div className="psm-mrp">{formatCurrency(Number(p.mrp || 0))}</div>
            <div className="psm-base">Base {formatCurrency(Number(p.basePrice || 0))}</div>
            {p.stockIndicator && (
              <div className={`psm-stk ${stkCls(p.stockIndicator)}`} style={{ marginTop: '6px', alignSelf: 'flex-end' }}>
                <span style={{ width: 6, height: 6, borderRadius: 3, background: stkDot(p.stockIndicator), display: 'inline-block', marginRight: 4 }} />
                {p.stockIndicator} {p.stockAvailable > 0 ? `(${p.stockAvailable})` : ''}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}, (p, n) => p.isSel === n.isSel && p.isExist === n.isExist && p.p === n.p);

/* ─── MAIN MODAL ─────────────────────────────────────────────────────────── */
export default function ProductSearchModal({
  isOpen,
  onClose,
  onAddProducts,
  existingIds = [],
  defaultQty = 1,
  defaultDiscountPercent = 0,
}) {
  const { masterProduct = [], getStatus } = useSelector(s => s.product);

  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const [fCol, setFCol] = useState([]);
  const [fFin, setFFin] = useState([]);
  const [fCat, setFCat] = useState([]);
  const [fSeg, setFSeg] = useState([]);
  const [fTyp, setFTyp] = useState([]);
  const [fPref, setFPref] = useState([]);

  const [qty, setQty] = useState(defaultQty);
  const [discPct, setDiscPct] = useState(defaultDiscountPercent);

  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setQuery(''); setSelected([]);
      setFCol([]); setFFin([]); setFCat([]); setFSeg([]); setFTyp([]); setFPref([]);
      setQty(defaultQty); setDiscPct(defaultDiscountPercent);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [isOpen, defaultQty, defaultDiscountPercent]);

  const opts = useMemo(() => {
    const colSet = new Set();
    const finSet = new Set();
    const catSet = new Set();
    const segSet = new Set();
    const typSet = new Set();
    const prefSet = new Set();

    const noFilters = fCol.length === 0 && fFin.length === 0 && fCat.length === 0 && fSeg.length === 0 && fTyp.length === 0;

    if (noFilters) {
      masterProduct.forEach(p => {
        if (p.collection) colSet.add(p.collection);
        if (p.finish && p.finish !== 'NOT FOUND') finSet.add(p.finish);
        if (p.category) catSet.add(p.category);
        if (p.priceSegment) segSet.add(p.priceSegment);
        if (p.product_typ_group) typSet.add(p.product_typ_group);
        if (p.preferences) prefSet.add(p.preferences);
      });
    } else {
      masterProduct.forEach(p => {
        const matchCol = fCol.length === 0 || fCol.includes(p.collection);
        const matchFin = fFin.length === 0 || fFin.includes(p.finish);
        const matchCat = fCat.length === 0 || fCat.includes(p.category);
        const matchSeg = fSeg.length === 0 || fSeg.includes(p.priceSegment);
        const matchTyp = fTyp.length === 0 || fTyp.includes(p.product_typ_group);
        const matchPref = fPref.length === 0 || fPref.includes(p.preferences);

        if (matchFin && matchCat && matchSeg && matchTyp && p.collection) colSet.add(p.collection);
        if (matchCol && matchCat && matchSeg && matchTyp && p.finish && p.finish !== 'NOT FOUND') finSet.add(p.finish);
        if (matchCol && matchFin && matchSeg && matchTyp && p.category) catSet.add(p.category);
        if (matchCol && matchFin && matchCat && matchTyp && p.priceSegment) segSet.add(p.priceSegment);
        if (matchCol && matchFin && matchCat && matchSeg && p.product_typ_group) typSet.add(p.product_typ_group);
        if (matchCol && matchFin && matchCat && matchSeg && matchTyp && p.preferences) prefSet.add(p.preferences);
      });
    }

    return {
      col: [...colSet].sort(),
      fin: [...finSet].sort(),
      cat: [...catSet].sort(),
      seg: [...segSet].sort(),
      typ: [...typSet].sort(),
      pref: [...prefSet].sort(),
    };
  }, [masterProduct, fCol, fFin, fCat, fSeg, fTyp, fPref]);

const activeCnt = [fCol, fFin, fCat, fSeg, fTyp, fPref].filter(a => a.length > 0).length; // <-- Update array

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    const noFilters = !q && fCol.length === 0 && fFin.length === 0 && fCat.length === 0 && fSeg.length === 0 && fTyp.length === 0 && fPref.length === 0; 

    if (noFilters) return masterProduct;

    return masterProduct.filter(p => {
      if (q && !(
        (p.sku_code || '').toLowerCase().includes(q) ||
        (p.product_desc || '').toLowerCase().includes(q) ||
        (p.brand || '').toLowerCase().includes(q) ||
        (p.collection || '').toLowerCase().includes(q)
      )) return false;
      if (fCol.length && !fCol.includes(p.collection)) return false;
      if (fFin.length && !fFin.includes(p.finish)) return false;
      if (fCat.length && !fCat.includes(p.category)) return false;
      if (fSeg.length && !fSeg.includes(p.priceSegment)) return false;
      if (fTyp.length && !fTyp.includes(p.product_typ_group)) return false;
      if (fPref.length && !fPref.includes(p.preferences)) return false; 
      return true;
    });
  }, [masterProduct, query, fCol, fFin, fCat, fSeg, fTyp, fPref]);

  const rowVirtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 100,
    overscan: 8,
  });

  const existingSet = useMemo(() => new Set(existingIds), [existingIds]);

  const toggle = useCallback(sku =>
    setSelected(prev => prev.includes(sku) ? prev.filter(s => s !== sku) : [...prev, sku]),
    []);

  const selectable = useMemo(
    () => filtered.filter(p => !existingSet.has(p.sku_code)),
    [filtered, existingSet],
  );
  const allSel = selected.length > 0 && selectable.length > 0 && selectable.every(p => selected.includes(p.sku_code));
  const someSel = !allSel && selected.length > 0 && selectable.some(p => selected.includes(p.sku_code));

  const toggleAll = useCallback(() => {
    const skus = selectable.map(p => p.sku_code);
    if (allSel) setSelected(prev => prev.filter(s => !skus.includes(s)));
    else setSelected(prev => [...prev, ...skus.filter(s => !prev.includes(s))]);
  }, [allSel, selectable]);

const clearAll = useCallback(() => {
    setFCol([]); setFFin([]); setFCat([]); setFSeg([]); setFTyp([]); setFPref([]); // <-- Add setFPref([])
  }, []);

  const handleAdd = useCallback(() => {
    const ss = new Set(selected);
    const products = masterProduct.filter(p => ss.has(p.sku_code)).map(p => ({
      productCode: p.sku_code || '', brand: p.brand || '',
      description: p.product_desc || '', collection: p.collection || '', pruning_date: p.pruning_date || '', preferences: p.preferences || '',
      status: p.status || '', productType: p.product_typ_group || '', category: p.category || '',
      priceSegment: p.priceSegment || '', finish: p.finish || '',
      image: p.image || null,
      linediagram_image: p.linediagram_image || null,
      mrp: Number(p.mrp) || 0, basePrice: Number(p.basePrice) || 0,
      gstRate: Number(p.gst) || 18, stock: Number(p.stockAvailable) || 0,
      stockIndicator: p.stockIndicator || '',
      quantity: Math.max(1, Number(qty) || 1),
      discountPercent: Number(discPct) || 0, discountPrice: 0,
    }));
    onAddProducts(products);
    onClose();
  }, [selected, masterProduct, qty, discPct, onAddProducts, onClose]);

  if (!isOpen) return null;

const activeChips = [
    ...fCol.map(v => ({ v, c: '#4F46E5', rm: () => setFCol(p => p.filter(x => x !== v)) })),
    ...fFin.map(v => ({ v, c: '#0D9488', rm: () => setFFin(p => p.filter(x => x !== v)) })),
    ...fCat.map(v => ({ v, c: '#0284C7', rm: () => setFCat(p => p.filter(x => x !== v)) })),
    ...fSeg.map(v => ({ v, c: '#D97706', rm: () => setFSeg(p => p.filter(x => x !== v)) })),
    ...fTyp.map(v => ({ v, c: '#6B7280', rm: () => setFTyp(p => p.filter(x => x !== v)) })),
    ...fPref.map(v => ({ v, c: '#7C3AED', rm: () => setFPref(p => p.filter(x => x !== v)) })), 
  ];

  return (
    <>
      <style>{CSS}</style>
      <div className="psm-bd" onClick={onClose}>
        <div className="psm-modal" onClick={e => e.stopPropagation()}>

          {/* HEADER */}
          <div className="psm-hd">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div className="psm-hd-icon"><Package size={16} color="white" /></div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span className="psm-hd-title">Product Catalog</span>
                  {filtered.length !== masterProduct.length && (
                    <span className="psm-hd-badge">{filtered.length.toLocaleString('en-IN')} matching</span>
                  )}
                </div>
                <div className="psm-hd-sub">
                  {masterProduct.length.toLocaleString('en-IN')} products available · select to add to quote
                </div>
              </div>
            </div>
            <button className="psm-hd-close" onClick={onClose}><X size={14} /></button>
          </div>

          {/* SEARCH + FILTER TOGGLE */}
          <div className="psm-sb">
            <div className="psm-sf">
              <Search size={14} color="var(--mu)" />
              <input ref={inputRef} className="psm-si" value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search by SKU, name, brand or collection…" />
              {query && (
                <button className="psm-sc" onClick={() => setQuery('')}>
                  <X size={10} color="var(--mu)" />
                </button>
              )}
            </div>
            <button className={`psm-fb${showFilters || activeCnt > 0 ? ' on' : ''}`}
              onClick={() => setShowFilters(f => !f)}>
              <SlidersHorizontal size={13} />
              Filters
              {activeCnt > 0 && <span className="psm-fc">{activeCnt}</span>}
            </button>
          </div>

          {/* FILTERS PANEL */}
          {showFilters && (
            <div className="psm-fp">
              <div className="psm-fr">
                <MultiSelectDropdown label="Collections" icon={Layers} options={opts.col} value={fCol} onChange={setFCol} />
                <MultiSelectDropdown label="Finishes" icon={Paintbrush} options={opts.fin} value={fFin} onChange={setFFin} />
                <MultiSelectDropdown label="Categories" icon={Grid2X2} options={opts.cat} value={fCat} onChange={setFCat} />
                <MultiSelectDropdown label="Segments" icon={BarChart3} options={opts.seg} value={fSeg} onChange={setFSeg} />
                <MultiSelectDropdown label="Types" icon={Tag} options={opts.typ} value={fTyp} onChange={setFTyp} />
                <MultiSelectDropdown label="Preference" icon={Tag} options={opts.pref} value={fPref} onChange={setFPref} />
              </div>
              {activeChips.length > 0 && (
                <div className="psm-chips">
                  {activeChips.map(({ v, c, rm }) => (
                    <span key={v} className="psm-chip" onClick={rm}
                      style={{ color: c, background: c + '12', borderColor: c + '35' }}>
                      {v} <X size={9} />
                    </span>
                  ))}
                  <button className="psm-ca" onClick={clearAll}>Clear all</button>
                </div>
              )}
            </div>
          )}

          {/* CONTROLS BAR */}
          <div className="psm-cb">
            <div className="psm-sa" onClick={toggleAll}>
              <CB checked={allSel} inter={someSel} />
              <span className="psm-sa-lbl">
                {allSel ? `Deselect all (${selectable.length})` : `Select all (${selectable.length})`}
              </span>
            </div>
            <div style={{ flex: 1 }} />
            <NumberInput label="Default Qty" value={qty} onChange={setQty} min={1} step={1} />
            <NumberInput label="Discount" value={discPct} onChange={setDiscPct} min={0} max={100} step={0.5} suffix="%" />
          </div>

          {/* VIRTUALISED LIST */}
          <div ref={scrollRef} className="psm-list">
            {masterProduct.length === 0 && (
              getStatus === 'pending' ? (
                <div style={{ width: '100%' }}>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <div key={i} className="flex items-center gap-4 p-3 border-b border-slate-100 w-full animate-pulse bg-white">
                      <div className="w-[18px] h-[18px] bg-slate-200 rounded shrink-0"></div>
                      <div className="w-[45px] h-[45px] bg-slate-100 rounded-md shrink-0"></div>
                      <div className="flex-1 space-y-2 py-1">
                        <div className="h-3.5 bg-slate-200 rounded w-1/4"></div>
                        <div className="h-2.5 bg-slate-100 rounded w-2/3"></div>
                      </div>
                      <div className="shrink-0 space-y-2">
                        <div className="h-3.5 bg-slate-200 rounded w-16 ml-auto"></div>
                        <div className="h-2.5 bg-slate-100 rounded w-12 ml-auto"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="psm-em">
                  <div style={{ padding: '24px', backgroundColor: '#f8fafc', borderRadius: '50%', marginBottom: '16px' }}>
                    <Package size={48} style={{ color: '#cbd5e1' }} strokeWidth={1.5} />
                  </div>
                  <span className="psm-em-t" style={{ color: '#334155', fontWeight: 600 }}>No products available</span>
                  <span className="psm-em-s" style={{ color: '#64748b' }}>The catalog is currently empty.</span>
                </div>
              )
            )}
            {masterProduct.length > 0 && filtered.length === 0 && (
              <div className="psm-em">
                <div style={{ padding: '24px', backgroundColor: '#f8fafc', borderRadius: '50%', marginBottom: '16px' }}>
                  <Search size={48} style={{ color: '#cbd5e1' }} strokeWidth={1.5} />
                </div>
                <span className="psm-em-t" style={{ color: '#334155', fontWeight: 600 }}>No products found</span>
                <span className="psm-em-s" style={{ color: '#64748b' }}>Try adjusting your search or removing some filters.</span>
              </div>
            )}
            {filtered.length > 0 && (
              <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}>
                {rowVirtualizer.getVirtualItems().map(vr => {
                  const p = filtered[vr.index];
                  const isSel = selected.includes(p.sku_code);
                  const isEx = existingSet.has(p.sku_code);
                  return (
                    <div key={p.sku_code} data-index={vr.index}
                      ref={rowVirtualizer.measureElement}
                      style={{
                        position: 'absolute', top: 0, left: 0, width: '100%',
                        transform: `translateY(${vr.start}px)`
                      }}>
                      <ProductRow p={p} isSel={isSel} isExist={isEx} onToggle={toggle} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div className="psm-ft">
            <span className="psm-fi">
              {selected.length > 0
                ? <><span className="psm-fic">{selected.length}</span> product{selected.length > 1 ? 's' : ''} selected</>
                : <><span className="psm-fit">{filtered.length.toLocaleString('en-IN')}</span> shown</>
              }
            </span>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="psm-bc" onClick={onClose}>Cancel</button>
              <button className="psm-ba" disabled={selected.length === 0} onClick={handleAdd}>
                <ShoppingCart size={14} />
                Add{selected.length > 0 ? ` ${selected.length}` : ''} to Quote
              </button>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}